import { LitElement, html, css, unsafeCSS } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { Plus, StickyNote, Trash2, X } from 'lucide';
import { confirmDialog } from '../utils/confirm-dialog.js';
import { api } from '../api.js';
import { lucideIcon } from '../icon.js';
import base from '../styles/base.css?inline';

/**
 * lab-pakketten — welke analyses het lab op welk mengmonster uitvoert.
 *
 * Een matrix en geen lijst per mengmonster: je bestelt zelden één analyse op één monster.
 * Meestal zet je hetzelfde pakket op alles, met hier en daar een uitzondering, en dat lees je
 * af aan een kolom vinkjes. Zo werkt het ook in TerraBase, waar deze gebruikers vandaan komen.
 *
 * De kolommen zijn geen instelling die we bewaren: ze volgen uit wat er ergens aanstaat, plus
 * wat je deze sessie hebt toegevoegd. Daardoor klopt de matrix ook na herladen, en verdwijnt
 * een kolom vanzelf zodra het laatste vinkje eruit gaat.
 *
 * De pakketten komen uit de productlijst van het lab (GetProductList), per klantcode en
 * monstersoort opgehaald en gecachet — dezelfde lijst waar de urgenties en afspraken uit
 * komen. Wat er ín een pakket zit staat er niet bij; dat zou het bestand megabytes groot
 * maken. Daarvoor is een aparte call (GetSuite) nodig, en die is puur informatief: voor het
 * versturen heeft het lab alleen de pakketcode nodig.
 *
 * De rijen komen uit het mandje van Samenstelling (`mengmonsters`) en niet uit de bewaarde
 * opdracht: een analysemonster dat je net hebt gemaakt staat er dan meteen, ook al is het nog
 * niet bewaard. Pakketten aanvinken kan pas als het bewaard is -- het pakket hangt aan het
 * analysemonster in de backend. Wat er aan de mengmonsters zelf verandert (notitie,
 * nieuw, weg) meldt dit onderdeel aan de pagina; die laat het mandje het doen, zodat er één
 * plek is die de mengmonsters beheert.
 *
 * Events: mm-nieuw, mm-verwijder, mm-notitie (detail: { code, ... }),
 * en order-changed na het opslaan van pakketten. Hernoemen gaat via de functie `hernoem`, omdat
 * het antwoord (lukt het, of bestaat de naam al) meteen bij het veld moet staan.
 */
export class LabPakketten extends LitElement {
  static properties = {
    order: { type: Object },
    /** Het mandje: [{ code, notitie, potjes: [{ id, naam, boringNaam, van, tot }] }]. */
    mengmonsters: { attribute: false },
    /** (code) => string[] -- de mengregels die dit mengmonster breekt. */
    waarschuwingen: { attribute: false },
    /** (code, naam) => string|null -- hernoemen; geeft een melding als het niet kan. */
    hernoem: { attribute: false },
    _hernoemt: { state: true },      // code van het mengmonster waarvan de naam openstaat
    _hernoemFout: { state: true },
    /** Verstuurde opdracht: kijken mag, wijzigen niet meer. */
    vergrendeld: { type: Boolean },
    _kolommen: { state: true },      // pakketten die je deze sessie hebt toegevoegd
    _kiezerOpen: { state: true },
    _zoek: { state: true },
    _resultaten: { state: true },
    _categorieen: { state: true },
    _categorie: { state: true },
    _zoekt: { state: true },
    _zoekfout: { state: true },
    _bezig: { state: true },
    _fout: { state: true },
    _notitieVoor: { state: true },   // het analysemonster waarvan de notitie openstaat
    _notitieTekst: { state: true },
  };

  constructor() {
    super();
    this.order = null;
    this._kolommen = [];
    this._kiezerOpen = false;
    this._zoek = '';
    this._resultaten = [];
    this._categorieen = [];
    this._categorie = '';
    this._zoekt = false;
    this._zoekfout = null;
    this._bezig = false;
    this._fout = null;
    this._notitieVoor = null;
    this._notitieTekst = '';
    this._meetSchuif = this._meetSchuif.bind(this);
  }

  disconnectedCallback() {
    clearTimeout(this._zoekTimer);
    this._meter?.disconnect();
    this._meter = null;
    super.disconnectedCallback();
  }

  updated() {
    const vak = this.renderRoot?.querySelector('.tabelvak');
    if (vak && !this._meter) {
      // Ook meten als het venster of de inhoud verandert: een kolom erbij kan de tabel
      // breder maken dan het scherm zonder dat er iets gescrold is.
      this._meter = new ResizeObserver(() => this._meetSchuif());
      this._meter.observe(vak);
    }
    this._meetSchuif();
  }

  /**
   * De schaduwen op de vaste kolommen laten zien dat er iets achter zit -- dus alleen als dat
   * ook zo is. Staan ze er altijd, dan betekenen ze niets en zie je op een korte tabel een
   * rand waar geen rand hoort.
   *
   * Buiten Lit om: dit gebeurt bij elke scrollstap, en daarvoor hoeft de tabel niet opnieuw
   * getekend te worden.
   */
  _meetSchuif() {
    const vak = this.renderRoot?.querySelector('.tabelvak');
    if (!vak) return;
    const speling = vak.scrollWidth - vak.clientWidth;
    vak.classList.toggle('links-verborgen', vak.scrollLeft > 1);
    vak.classList.toggle('rechts-verborgen', speling > 1 && vak.scrollLeft < speling - 1);
  }

  /** De bewaarde analysemonsters: die dragen de pakketten. */
  get _monsters() {
    return this.order?.analysisSamples || [];
  }

  /**
   * Eén rij per mengmonster uit het mandje, met het bewaarde analysemonster erbij (op id, want
   * de naam kun je wijzigen).
   * Zonder mandje -- dat gebeurt alleen als de pagina het nog niet heeft -- de bewaarde.
   */
  get _rijen() {
    const opId = new Map(this._monsters.map(s => [s.id, s]));
    if (!Array.isArray(this.mengmonsters)) {
      return this._monsters.map(s => ({
        code: s.name, notitie: s.reason || '', sample: s,
        potjes: (s.parts || []).map(p => ({ id: p.monsterId || p.id, naam: p.observations?.naam || p.barcode || '—' })),
      }));
    }
    return this.mengmonsters.map(m => ({ ...m, sample: (m.id && opId.get(m.id)) || null }));
  }

  /**
   * De naam ter plekke wijzigen: Enter of wegklikken bewaart, Escape laat het. Lukt het niet
   * (leeg, of de naam bestaat al), dan blijft het veld open met de reden eronder.
   */
  _openHernoem(rij) {
    this._hernoemt = rij.code;
    this._hernoemFout = '';
    this.updateComplete.then(() => {
      const veld = this.renderRoot.querySelector('.naamveld');
      veld?.focus();
      veld?.select();
    });
  }

  _bewaarHernoem(code, naam) {
    if (this._hernoemt !== code) return;
    const fout = this.hernoem?.(code, naam) || null;
    if (fout) { this._hernoemFout = fout; return; }
    this._hernoemt = null;
    this._hernoemFout = '';
  }

  _meld(naam, detail = {}) {
    this.dispatchEvent(new CustomEvent(naam, { detail, bubbles: true, composed: true }));
  }

  /**
   * Een analysemonster weggooien. Vragen als er iets in zit: de potjes komen weer vrij, maar de
   * gekozen pakketten zijn dan weg.
   */
  async _verwijderRij(rij) {
    const pakketten = (rij.sample?.packages || []).length;
    if (rij.potjes.length || pakketten) {
      const delen = [
        rij.potjes.length ? `${rij.potjes.length} ${rij.potjes.length === 1 ? 'potje komt' : 'potjes komen'} weer vrij` : '',
        pakketten ? `${pakketten} ${pakketten === 1 ? 'gekozen pakket verdwijnt' : 'gekozen pakketten verdwijnen'}` : '',
      ].filter(Boolean);
      const ok = await confirmDialog({
        title: `${rij.code} weggooien?`,
        message: `${delen.join(' en ')}.`,
        confirmLabel: 'Ja, weggooien',
        danger: true,
      });
      if (!ok) return;
    }
    this._meld('mm-verwijder', { code: rij.code });
  }

  /**
   * Waarop twee pakketten dezelfde zijn: de code, en anders de naam.
   *
   * Vergelijken op code, want namen zijn niet uniek -- "Lood" bestaat als GRN-PB én als
   * AG3-PB. Pakketten uit de bouwfase dragen alleen een naam; die vielen door de codevergelijking
   * uit de matrix en waren daardoor niet meer weg te klikken, terwijl de controle er wel over
   * klaagde. Vandaar de terugval op de naam.
   */
  _sleutel(pkg) {
    return pkg?.code ? `code:${pkg.code}` : `naam:${String(pkg?.name || '').toLowerCase()}`;
  }

  /** Elk pakket dat ergens aanstaat, plus wat je zelf hebt toegevoegd. */
  get _zichtbareKolommen() {
    const kolommen = new Map(this._kolommen.map(p => [this._sleutel(p), p]));
    for (const sample of this._monsters) {
      for (const pkg of sample.packages || []) {
        const sleutel = this._sleutel(pkg);
        if (!kolommen.has(sleutel)) kolommen.set(sleutel, pkg);
      }
    }
    return [...kolommen.values()];
  }

  /** Staat dit pakket aan bij dit monster? */
  _staatAan(sample, pkg) {
    return (sample.packages || []).some(gekozen => this._sleutel(gekozen) === this._sleutel(pkg));
  }

  async _wissel(sample, pkg) {
    const huidig = sample.packages || [];
    const packages = this._staatAan(sample, pkg)
      ? huidig.filter(gekozen => this._sleutel(gekozen) !== this._sleutel(pkg))
      : [...huidig, { code: pkg.code, name: pkg.name }];
    await this._opslaan([[sample, { packages }]]);
  }

  /**
   * Een reeks wijzigingen opslaan en daarna één keer de opdracht ophalen.
   *
   * Per monster verversen zou bij een hele kolom twintig keer dezelfde opdracht ophalen, en
   * dan flikkert de matrix mee met elke tussenstand.
   */
  async _opslaan(wijzigingen) {
    if (this.vergrendeld || !wijzigingen.length) return;
    this._bezig = true;
    this._fout = null;
    try {
      for (const [sample, velden] of wijzigingen) {
        await api.updateLabAnalysisSample(sample.id, velden);
      }
      const res = await api.getLabOrder(this.order.id);
      // De pagina eromheen houdt de opdracht vast; die moet mee, anders lopen de tellers
      // achter de tabbladen achter op wat je net hebt aangevinkt.
      this.dispatchEvent(new CustomEvent('order-changed', {
        detail: { labOrder: res.labOrder }, bubbles: true, composed: true,
      }));
    } catch (e) {
      this._fout = e.message || 'Pakket wijzigen mislukt';
    } finally {
      this._bezig = false;
    }
  }

  /** Een heel mengmonster aan- of uitzetten voor dit pakket: de kolomkop is ook een knop. */
  async _wisselKolom(pkg) {
    const allemaal = this._monsters.every(s => this._staatAan(s, pkg));
    await this._opslaan(this._monsters.map(sample => [sample, {
      packages: allemaal
        ? (sample.packages || []).filter(g => this._sleutel(g) !== this._sleutel(pkg))
        : this._staatAan(sample, pkg) ? sample.packages : [...(sample.packages || []), { code: pkg.code, name: pkg.name }],
    }]));
  }

  /**
   * Een kolom weghalen. Staat het pakket ergens aan, dan gooi je die keuzes weg -- vandaar de
   * vraag. Staat het nergens aan, dan is de kolom alleen iets wat je deze sessie hebt
   * opgezocht en kan hij zonder meer weg.
   */
  async _verwijderKolom(pkg, e) {
    e?.stopPropagation();
    const aan = this._monsters.filter(s => this._staatAan(s, pkg));
    if (aan.length) {
      const ok = await confirmDialog({
        title: `${pkg.name} weghalen?`,
        message: `Dit pakket staat aan bij ${aan.length} ${aan.length === 1 ? 'mengmonster' : 'mengmonsters'}. Die keuze gaat weg.`,
        confirmLabel: 'Ja, weghalen',
        danger: true,
      });
      if (!ok) return;
    }
    this._kolommen = this._kolommen.filter(k => this._sleutel(k) !== this._sleutel(pkg));
    await this._opslaan(aan.map(sample => [
      sample, { packages: (sample.packages || []).filter(g => this._sleutel(g) !== this._sleutel(pkg)) },
    ]));
  }

  /**
   * Waarom juist deze potjes samen gaan. Dat staat nergens anders: de potjes vertellen wat er
   * in zit, niet waarom ze één pot zijn geworden. Een half jaar later, bij een vraag over de
   * uitslag, is dat het eerste wat je wilt weten.
   */
  _openNotitie(rij) {
    this._notitieVoor = rij;
    this._notitieTekst = rij.notitie || '';
  }

  _bewaarNotitie() {
    const rij = this._notitieVoor;
    const tekst = this._notitieTekst.trim();
    this._notitieVoor = null;
    if ((rij.notitie || '') !== tekst) this._meld('mm-notitie', { code: rij.code, tekst });
  }

  _renderNotitievenster() {
    const rij = this._notitieVoor;
    if (!rij) return '';
    return html`
      <div class="overlay" @click=${e => { if (e.target === e.currentTarget) this._notitieVoor = null; }}>
        <div class="venster notitie-venster">
          <header>
            <div><strong>Notitie bij ${rij.code}</strong></div>
            <button class="sluit" @click=${() => { this._notitieVoor = null; }}>${unsafeSVG(lucideIcon(X, 16))}</button>
          </header>
          <div class="venster-lijf">
            <textarea rows="5" .value=${this._notitieTekst}
                      @input=${e => { this._notitieTekst = e.target.value; }}></textarea>
            <div class="notitieknoppen">
              <button class="tekstknop" @click=${() => { this._notitieVoor = null; }}>Annuleren</button>
              <button class="knop vol" ?disabled=${this._bezig} @click=${() => this._bewaarNotitie()}>Notitie opslaan</button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  _openKiezer() {
    this._kiezerOpen = true;
    this._zoek = '';
    this._categorie = '';
    this._zoekfout = null;
    this._zoeken();
  }

  _sluitKiezer() {
    this._kiezerOpen = false;
    clearTimeout(this._zoekTimer);
  }

  /**
   * Zoeken gebeurt op de server, niet in de browser: deze klant heeft ~1900 pakketten per
   * monstersoort, en daar kiest iemand er hooguit vijf uit.
   */
  _typt(waarde) {
    this._zoek = waarde;
    clearTimeout(this._zoekTimer);
    this._zoekTimer = setTimeout(() => this._zoeken(), 250);
  }

  async _zoeken() {
    const connectionId = this.order?.labConnectionId;
    if (!connectionId) {
      this._zoekfout = 'Deze opdracht heeft nog geen laboratorium.';
      this._resultaten = [];
    this._categorieen = [];
    this._categorie = '';
      return;
    }
    this._zoekt = true;
    this._zoekfout = null;
    try {
      const res = await api.getLabPackages(connectionId, {
        matrix: this.order.sampleType || 'grond',
        certificateFormat: this.order.certificateFormat || '',
        query: this._zoek,
        category: this._categorie,
      });
      this._resultaten = res.packages || [];
      this._categorieen = res.categories || [];
      // Een lege lijst zonder zoekterm betekent niet "niets gevonden" maar "deze matrix
      // kennen we nog niet bij het lab"; de server zegt er dan iets over.
      if (res.message) this._zoekfout = res.message;
    } catch (e) {
      this._zoekfout = e.message || 'Pakketten ophalen mislukt';
      this._resultaten = [];
    this._categorieen = [];
    this._categorie = '';
    } finally {
      this._zoekt = false;
    }
  }

  /** De uitslag per categorie, in de volgorde die de server geeft. */
  get _gegroepeerd() {
    const groepen = new Map();
    for (const pkg of this._resultaten) {
      const naam = pkg.categoryName || 'Overig';
      if (!groepen.has(naam)) groepen.set(naam, []);
      groepen.get(naam).push(pkg);
    }
    return [...groepen.entries()];
  }

  _voegKolomToe(pkg) {
    if (!this._kolommen.some(gekozen => this._sleutel(gekozen) === this._sleutel(pkg))) {
      this._kolommen = [...this._kolommen, { code: pkg.code, name: pkg.name }];
    }
    this._sluitKiezer();
  }

  render() {
    const kolommen = this._zichtbareKolommen;
    const rijen = this._rijen;
    const potjes = rijen.reduce((n, r) => n + r.potjes.length, 0);
    const kan = !this.vergrendeld;
    return html`
      <section class="paneel">
        <header class="kop">
          <span class="mv-caps">Analysemonsters &amp; pakketten</span>
          <span class="spacer"></span>
          <span class="telling">
            ${rijen.length} ${rijen.length === 1 ? 'analysemonster' : 'analysemonsters'} ·
            ${potjes} ${potjes === 1 ? 'potje' : 'potjes'}
          </span>
        </header>

        ${this._fout ? html`<p class="fout">${this._fout}</p>` : ''}

        ${!rijen.length ? html`
          <div class="leeg">
            <strong>Nog geen analysemonsters</strong>
            <span>Klik potjes aan in de boorprofielen bij Samenstelling, of maak hier een leeg analysemonster
                  en vul het daarna.</span>
          </div>
        ` : html`
          <div class="tabelvak" @scroll=${this._meetSchuif}>
            <table>
              <thead>
                <tr>
                  <th class="mm">Analysemonster</th>
                  <th class="matrixkolom">Monstertype</th>
                  ${kolommen.map(pkg => html`
                    <th class="pakket ${pkg.code ? '' : 'zondercode'}">
                      <button class="kolomknop" title=${pkg.code
                                ? `${pkg.name} (${pkg.code}) — aan of uit voor alle analysemonsters`
                                : `${pkg.name} — dit pakket heeft geen labcode en kan niet verstuurd worden; haal het weg en kies het opnieuw`}
                              @click=${() => this._wisselKolom(pkg)} ?disabled=${this._bezig || !kan}>
                        <span class="pakketnaam">${pkg.name}</span>
                        <code>${pkg.code || 'geen labcode'}</code>
                      </button>
                      ${kan ? html`
                      <button class="kolomweg" title="Deze kolom weghalen" ?disabled=${this._bezig}
                              @click=${(e) => this._verwijderKolom(pkg, e)}>${unsafeSVG(lucideIcon(X, 12))}</button>` : ''}
                    </th>`)}
                  <th class="toevoegen">
                    ${kan ? html`
                      <button class="tekstknop groen" @click=${this._openKiezer}>+ Pakket</button>`
                      : html`<span class="slot">vastgelegd</span>`}
                  </th>
                </tr>
              </thead>
              <tbody>
                ${rijen.map(rij => this._renderRij(rij, kolommen, kan))}
              </tbody>
            </table>
          </div>
          ${!kolommen.length ? html`
            <p class="hint">Nog geen pakketten gekozen. Zoek er een bij het lab via <strong>+ Pakket</strong>.</p>
          ` : ''}
        `}

        ${kan ? html`
          <footer class="voet">
            <button class="hoofdknop" @click=${() => this._meld('mm-nieuw')}>
              ${unsafeSVG(lucideIcon(Plus, 14))} Analysemonster toevoegen
            </button>
          </footer>` : ''}
      </section>
      ${this._renderKiezer()}
      ${this._renderNotitievenster()}
    `;
  }

  /**
   * Eén analysemonster: waar het uit bestaat, en per pakket een vinkje.
   *
   * Het stipje is groen, of rood als het mengmonster een mengregel breekt; welke regel staat
   * eronder. De potjes staan hier vast: wat erin zit pas je aan bij Samenstelling.
   */
  _renderRij(rij, kolommen, kan) {
    const sample = rij.sample;
    const breekt = this.waarschuwingen?.(rij.code) || [];
    return html`
      <!-- Geen 'actief' mengmonster hier: dat hoort bij Samenstelling, waar je potjes aanklikt.
           Bij Pakketten liggen de mengmonsters vast. -->
      <tr>
        <td class="mm">
          <div class="mmkop">
            <span class="stip ${breekt.length ? 'rood' : ''}"></span>
            ${this._hernoemt === rij.code ? html`
              <input class="naamveld" .value=${rij.code} maxlength="40" aria-label="Naam van het mengmonster"
                     @keydown=${(e) => {
                       if (e.key === 'Enter') { e.preventDefault(); this._bewaarHernoem(rij.code, e.target.value); }
                       if (e.key === 'Escape') { this._hernoemt = null; this._hernoemFout = ''; }
                     }}
                     @blur=${(e) => this._bewaarHernoem(rij.code, e.target.value)}>
            ` : html`
              <!-- Klik op de naam om hem aan te passen, net als bij Samenstelling. -->
              ${kan ? html`
                <button class="mmnaam" title="Klik om de naam aan te passen" @click=${() => this._openHernoem(rij)}>${rij.code}</button>`
                : html`<span class="mmnaam">${rij.code}</span>`}`}
            ${kan || rij.notitie ? html`
              <button class="notitie ${rij.notitie ? 'gevuld' : ''}"
                      title=${rij.notitie ? `Notitie: ${rij.notitie}` : 'Notitie toevoegen — bijvoorbeeld waarom juist deze potjes samen gaan'}
                      @click=${() => this._openNotitie(rij)}>
                ${unsafeSVG(lucideIcon(StickyNote, 13))}
              </button>` : ''}
          </div>
          ${this._hernoemt === rij.code && this._hernoemFout ? html`<div class="naamfout">${this._hernoemFout}</div>` : ''}
          <div class="potjes">
            ${rij.potjes.length ? rij.potjes.map(p => html`
              <span class="potje" title=${`${p.boringNaam ? `${p.boringNaam} · ` : ''}${p.van ?? ''}–${p.tot ?? ''} cm · aanpassen bij Samenstelling`}>${p.naam}</span>`)
              : html`<small>nog geen potjes · klik ze aan bij Samenstelling</small>`}
          </div>
          ${breekt.length ? html`<ul class="waarsch">${breekt.map(w => html`<li>${w}</li>`)}</ul>` : ''}
        </td>
        <td class="matrixkolom">${sample?.matrix === 'water' ? 'grondwater' : (sample?.matrix || (rij.potjes.some(p => p.matrix === 'water' || p.matrix === 'grondwater') ? 'grondwater' : 'grond'))}</td>
        ${kolommen.map(pkg => html`
          <td class="vink">
            <button class="vinkje ${sample && this._staatAan(sample, pkg) ? 'aan' : ''}"
                    title=${sample ? `${pkg.name} voor ${rij.code}` : `${rij.code} wordt nog bewaard`}
                    ?disabled=${this._bezig || !kan || !sample}
                    @click=${() => this._wissel(sample, pkg)}></button>
          </td>`)}
        <td class="toevoegen">
          ${kan ? html`
            <button class="weg" title=${`${rij.code} weggooien`} @click=${() => this._verwijderRij(rij)}>
              ${unsafeSVG(lucideIcon(Trash2, 15))}
            </button>` : ''}
        </td>
      </tr>`;
  }

  /**
   * Het zoekvenster. De categorie staat bij elk resultaat, want de codes zeggen op zichzelf
   * weinig: GRN-PB en AG3-PB heten allebei "Lood" en verschillen alleen in of er AS3000-erkend
   * gewerkt wordt.
   */
  _renderKiezer() {
    if (!this._kiezerOpen) return '';
    return html`
      <div class="overlay" @click=${e => { if (e.target === e.currentTarget) this._sluitKiezer(); }}>
        <div class="venster">
          <header>
            <div>
              <strong>Pakket toevoegen</strong>
              <span>${this.order?.sampleType || 'grond'}</span>
            </div>
            <button class="sluit" @click=${this._sluitKiezer}>${unsafeSVG(lucideIcon(X, 16))}</button>
          </header>
          <div class="venster-lijf">
            <div class="zoekrij">
              <input class="zoek mv-field" type="search" autofocus
                     placeholder="Zoek op naam of code, bijvoorbeeld metalen of AG3-MET-8"
                     .value=${this._zoek} @input=${e => this._typt(e.target.value)}>
              <!-- Het lab zet in deze lijst ook losse bepalingen en verrichtingen (malen,
                   steekbussen, doorbelasting transport). Met de categorie erbij zie je wat
                   iets is, en hiermee kies je alleen de groep die je zoekt. -->
              <select class="mv-field mv-select" .value=${this._categorie} @change=${e => { this._categorie = e.target.value; this._zoeken(); }}>
                <option value="">Alle categorieën</option>
                ${this._categorieen.map(c => html`
                  <option value=${c.code} ?selected=${this._categorie === c.code}>${c.name} (${c.count})</option>`)}
              </select>
            </div>
            ${this._zoekfout ? html`<p class="fout">${this._zoekfout}</p>` : ''}
            <div class="resultaten">
              ${this._zoekt ? html`<p class="hint">Zoeken…</p>`
                : this._resultaten.length ? this._gegroepeerd.map(([categorie, pakketten]) => html`
                    <div class="groep">${categorie}</div>
                    ${pakketten.map(pkg => html`
                      <button class="resultaat" @click=${() => this._voegKolomToe(pkg)}>
                        <span class="naam">${pkg.name}</span>
                        <code>${pkg.code}</code>
                      </button>`)}`)
                : this._zoekfout ? '' : html`<p class="hint">Geen pakket gevonden voor deze zoekterm.</p>`}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  static styles = css`${unsafeCSS(base)}
    :host { display:block; }
    .paneel { border:1px solid var(--mv-border); border-radius:12px; background:var(--mv-surface); overflow:hidden; }
    .kop { display:flex; align-items:center; gap:10px; flex-wrap:wrap; padding:16px 20px 14px;
           border-bottom:1px solid var(--mv-border-soft); }
    /* Donkergroen, net als de koppen 'Veldgegevens' en 'Mengmonsters' bij Samenstelling. */
    .kop { background:#22371f; border-bottom:none; padding:14px 20px 12px; }
    .kop .mv-caps { color:#a9c69f; font-size:.66rem; }
    .kop .telling { color:#cfe0c9; font-weight:700; font-size:.72rem; line-height:1.3; }
    .mv-caps { font-size:.7rem; font-weight:900; letter-spacing:.08em; color:var(--mv-text-sub); text-transform:uppercase; }
    .uitleg, .telling { font-size:.75rem; color:var(--mv-text-muted); }
    .spacer { flex:1; }
    .fout { margin:10px 16px 0; padding:6px 10px; border-radius:6px; background:var(--mv-danger-bg);
            color:var(--mv-danger); font-size:.78rem; font-weight:700; }
    .hint { margin:10px 16px 14px; font-size:.78rem; color:var(--mv-text-muted); }
    .leeg { display:flex; flex-direction:column; align-items:center; gap:6px; padding:44px 20px; text-align:center; }
    .leeg strong { font-size:.88rem; color:var(--mv-text); }
    .leeg span { font-size:.78rem; color:var(--mv-text-muted); max-width:420px; }

    .tabelvak { overflow-x:auto; }
    /* Niet uitrekken maar zo breed als hij nodig heeft: met width:100% kneep de browser de
       kolommen samen tot het mengmonster op één letter stond en de knop uit beeld liep. Nu
       schuift de tabel opzij, en blijven de eerste en de laatste kolom staan. */
    table { width:max-content; min-width:100%; border-collapse:separate; border-spacing:0; }
    th, td { padding:10px 12px; text-align:left; border-bottom:1px solid var(--mv-border-soft); vertical-align:middle; }
    th { font-size:.68rem; font-weight:800; letter-spacing:.04em; color:var(--mv-text-sub);
         text-transform:uppercase; background:var(--mv-surface-alt); white-space:nowrap; }

    /* Links het mengmonster, rechts de knop: allebei blijven ze staan tijdens het schuiven.
       De schaduw aan hun binnenrand verschijnt pas als er werkelijk iets achter schuift;
       zie _meetSchuif. */
    th.mm, td.mm { position:sticky; left:0; z-index:2; min-width:275px; }
    th.toevoegen, td.toevoegen { position:sticky; right:0; z-index:2; }
    .tabelvak.links-verborgen :is(th, td).mm { box-shadow:6px 0 8px -7px rgba(16,28,18,.35); }
    .tabelvak.rechts-verborgen :is(th, td).toevoegen { box-shadow:-6px 0 8px -7px rgba(16,28,18,.35); }
    td.mm, td.toevoegen { background:var(--mv-surface); }
    th.mm, th.toevoegen { z-index:3; }
    /* Kop en vinkje delen dezelfde vaste kolombreedte, anders schuift het vinkje op zodra de
       ene pakketnaam langer is dan de andere. */
    th.pakket, td.vink { width:140px; min-width:140px; text-align:center; }
    th.pakket { position:relative; }
    /* Pas zichtbaar als je de kolom aanwijst: een rij kruisjes boven de matrix nodigt uit om
       er per ongeluk een aan te tikken, en weghalen is niet wat je hier meestal komt doen. */
    .kolomweg { position:absolute; top:3px; right:3px; display:flex; padding:2px; border:none;
                border-radius:4px; background:none; color:var(--mv-text-muted); cursor:pointer;
                opacity:0; transition:opacity .12s ease; }
    th.pakket:hover .kolomweg, .kolomweg:focus-visible { opacity:1; }
    .kolomweg:hover { background:var(--mv-danger-bg); color:var(--mv-danger); }
    /* Het mengmonster dat in Samenstelling gevuld wordt, licht groen: zo zie je waar je
       potjes heen gaan als je terugschakelt. */
    .mmkop { display:flex; align-items:center; gap:8px; }
    .stip { width:7px; height:7px; flex:none; border-radius:50%; background:var(--mv-primary); }
    .stip.rood { background:var(--mv-danger); }
    .mmnaam { border:none; background:none; padding:0; font:inherit; font-size:.88rem; font-weight:800; color:var(--mv-text); }
    button.mmnaam { cursor:text; }
    button.mmnaam:hover { text-decoration:underline dotted; text-underline-offset:3px; }
    .naamveld { width:200px; box-sizing:border-box; padding:4px 8px; border:1.5px solid var(--mv-primary);
                border-radius:6px; background:var(--mv-surface); color:var(--mv-text);
                font:inherit; font-size:.86rem; font-weight:800; outline:none; }
    .naamfout { margin-top:4px; font-size:.7rem; font-weight:700; color:var(--mv-danger); }
    .notitie { display:flex; padding:2px; border:none; border-radius:4px; background:none;
               color:var(--mv-text-muted); cursor:pointer; }
    .notitie.gevuld { color:var(--mv-primary); }
    .notitie:hover { background:var(--mv-bg); color:var(--mv-text-sub); }
    .waarsch { margin:6px 0 0; padding:4px 8px 4px 20px; border-radius:6px; background:var(--mv-danger-bg);
               color:var(--mv-danger); font-size:.66rem; font-weight:700; line-height:1.4; max-width:420px; white-space:normal; }
    .weg { display:flex; padding:4px; border:none; border-radius:6px; background:none; color:var(--mv-text-muted); cursor:pointer; }
    .weg:hover { background:var(--mv-danger-bg); color:var(--mv-danger); }
    .tekstknop.groen { color:var(--mv-primary); font-size:.76rem; font-weight:800; white-space:nowrap; text-transform:none; letter-spacing:0; }
    .tekstknop.groen:hover { color:var(--mv-primary-hover); }
    .voet { display:flex; align-items:center; gap:12px; padding:14px 16px; border-top:1px solid var(--mv-border-soft); }
    .hoofdknop { display:inline-flex; align-items:center; gap:8px; padding:9px 15px; border:none; border-radius:9px;
                 background:var(--mv-primary); color:#fff; font:inherit; font-size:.82rem; font-weight:800; cursor:pointer; }
    .hoofdknop:hover { background:var(--mv-primary-hover); }
    .notitie-venster textarea { width:100%; box-sizing:border-box; padding:8px 10px; border-radius:8px;
                                border:1px solid var(--mv-border); background:var(--mv-bg);
                                font:inherit; font-size:.82rem; resize:vertical; }
    .notitieknoppen { display:flex; align-items:center; justify-content:flex-end; gap:12px; margin-top:10px; }
    .tekstknop { border:none; background:none; padding:0; font:inherit; font-size:.76rem;
                 font-weight:700; color:var(--mv-text-sub); cursor:pointer; }
    .slot { font-size:.66rem; font-weight:800; color:var(--mv-text-muted); }
    .knop.vol { border-style:solid; border-color:var(--mv-primary); background:var(--mv-primary); color:#fff; }
    .knop.vol:hover { background:#2f6122; }
    /* Breed genoeg voor drie potjes naast elkaar; smaller worden ze niet, dan scrolt de rest. */
    .potjes { display:flex; flex-wrap:wrap; gap:4px; margin-top:6px; max-width:420px; }
    .potje { font:inherit; font-size:.64rem; font-weight:800; padding:2px 7px; border-radius:5px;
             background:var(--mv-primary-tint); border:1px solid var(--mv-border); color:var(--mv-primary-hover); cursor:default; }
    .potjes small { font-size:.68rem; color:var(--mv-text-muted); font-style:italic; }
    td.matrixkolom { font-size:.8rem; color:var(--mv-text-sub); }

    /* De kolomkop is ook een knop: hetzelfde pakket op alles zetten is de normale handeling,
       en dat twintig keer los aanvinken is werk dat de computer hoort te doen. */
    .kolomknop { display:flex; width:100%; flex-direction:column; align-items:center; gap:1px; border:none;
                 background:none; padding:2px 4px; border-radius:6px; font:inherit; cursor:pointer;
                 color:var(--mv-text); text-transform:none; letter-spacing:0; }
    .kolomknop:hover:not(:disabled) { background:var(--mv-surface); }
    .pakketnaam { font-size:.76rem; font-weight:800; max-width:100%; overflow:hidden;
                  text-overflow:ellipsis; white-space:nowrap; }
    .kolomknop code { font-size:.62rem; font-weight:600; color:var(--mv-text-muted); }
    /* Een pakket uit de bouwfase: wel een naam, geen labcode. Het lab kan er niets mee, dus
       het valt op tot je het vervangt. */
    .pakket.zondercode { background:var(--mv-danger-bg); }
    .pakket.zondercode .kolomknop code { color:var(--mv-danger); }

    /* Uit is lichtgrijs met een duidelijke rand: wit op een witte rij was nauwelijks te zien,
       en dan lijkt een lege matrix op een kapotte matrix. */
    .vinkje { display:block; margin:0 auto; width:20px; height:20px; border-radius:5px;
              border:1.5px solid var(--mv-border-strong, #d5dbe0); background:var(--mv-bg); cursor:pointer; }
    .vinkje:hover:not(:disabled) { border-color:var(--mv-primary); background:rgba(75,157,53,.08); }
    .vinkje.aan { background:var(--mv-primary); border-color:var(--mv-primary);
                  background-image:url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3.4' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m5 12.5 4.5 4.5L19 7'/%3E%3C/svg%3E");
                  background-size:13px; background-position:center; background-repeat:no-repeat; }
    .vinkje:disabled { opacity:.5; cursor:default; }

    .knop { display:inline-flex; align-items:center; gap:6px; padding:5px 10px; border-radius:8px;
            border:1.5px dashed #b7c9b1; background:var(--mv-surface); color:var(--mv-primary);
            font:inherit; font-size:.72rem; font-weight:800; cursor:pointer; white-space:nowrap;
            text-transform:none; letter-spacing:0; }
    .knop:hover { border-color:var(--mv-primary); background:rgba(75,157,53,.05); }

    .overlay { position:fixed; inset:0; background:rgba(15,23,42,.45); display:flex;
               align-items:flex-start; justify-content:center; padding:8vh 2vw; z-index:1200; }
    /* De zoeklijst hangt bovenaan omdat hij lang wordt; een notitie van vijf regels hoort
       gewoon in het midden. */
    .overlay:has(.notitie-venster) { align-items:center; padding:3vh 2vw; }
    .venster { width:min(560px, 100%); max-height:80vh; display:flex; flex-direction:column;
               background:var(--mv-surface); border-radius:12px; box-shadow:0 24px 60px rgba(0,0,0,.25); }
    .venster header { display:flex; align-items:flex-start; gap:10px; padding:14px 16px;
                      border-bottom:1px solid var(--mv-border); }
    .venster header strong { display:block; font-size:.95rem; }
    .venster header span { font-size:.72rem; color:var(--mv-text-muted); }
    .sluit { margin-left:auto; border:none; background:none; color:var(--mv-text-sub); cursor:pointer; display:flex; }
    .venster-lijf { padding:14px 16px 16px; overflow:auto; }
    .zoekrij { display:flex; gap:8px; }
    /* Opmaak van beide velden: .mv-field en .mv-select uit base.css, zoals elders. Deze keuzelijst
       had eigen regels zonder appearance:none, waardoor de browser zijn eigen pijltje tegen de
       rechterrand tekende. Hier alleen nog de maat. */
    .zoek { flex:1; min-width:0; box-sizing:border-box; font-size:.82rem; }
    .zoekrij select { max-width:220px; box-sizing:border-box; font-size:.82rem; font-weight:700; cursor:pointer; }
    .groep { position:sticky; top:0; padding:7px 10px 4px; background:var(--mv-surface);
             font-size:.62rem; font-weight:800; letter-spacing:.08em; text-transform:uppercase;
             color:var(--mv-text-muted); }
    .resultaten { margin-top:10px; display:flex; flex-direction:column; }
    .resultaat { display:flex; align-items:center; gap:10px; padding:8px 10px; border:none;
                 border-bottom:1px solid var(--mv-border-soft); background:none; font:inherit;
                 text-align:left; cursor:pointer; }
    .resultaat:hover { background:var(--mv-surface-alt); }
    .naam { flex:1; font-size:.82rem; font-weight:700; color:var(--mv-text); }
    .resultaat code { font-size:.68rem; font-weight:700; color:var(--mv-text-muted); }
  `;
}

customElements.define('lab-pakketten', LabPakketten);
